import { Component, EventEmitter, Input, Output, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { catchError, finalize, of, Subject, Subscription, takeUntil } from 'rxjs';
import { MyConfigOrganization } from '../../../../models/myConfig';
import { AdditionalServicePaginationModel, Order, dateRange } from '../../../../models/orderModel';
import { MatSort, SortDirection } from '@angular/material/sort';
import { MatPaginator } from '@angular/material/paginator';
import { AdminService } from '../../../../services/admin.service';
import { BackOfficeService } from '../../../../services/backoffice.service';
import { MatDialog } from '@angular/material/dialog';
import { FormGroup, UntypedFormBuilder } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { Router } from '@angular/router';
import { BackOffice24StatesService } from '../state/back-office24-states.service';

@Component({
  selector: 'app-additional-service-tasks',
  standalone: false,
  templateUrl: './additional-service-tasks.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './additional-service-tasks.component.css'
})
export class AdditionalServiceTasksComponent {
  private orgSubscription: Subscription = new Subscription();
  private destroy$ = new Subject<void>();

  @Input() organization: MyConfigOrganization;

  displayedColumns: string[] = [
    'parkingName',
    'orderNr',
    'regNo',
    'name',
    'from',
    'to',
    'planned',
    'status'
  ];

  @Output() incrementRequests: EventEmitter<void> = new EventEmitter<void>();
  @Output() decrementRequests: EventEmitter<void> = new EventEmitter<void>();

  @Input() isLoading: boolean = false;
  activeRequests: number = 0;
  orders: Order[] = [];
  ordersLastUpdated: any;
  ordersASLastUpdated: any;
  dataSource: MatTableDataSource<Order>;

  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: false }) set matSort(sort: MatSort) {
    if (sort) {
      this.sort = sort;
      this.setupSorting();
    }
  }
  sort: MatSort;

  totalData: number = 0;
  pageSizes: number[] = [25, 50, 100];


  private readonly columnMapping: { [key: string]: string } = {
    'orderNr': 'OrderNr',
    'regNo': 'RegNo',
    'parkingName': 'ParkingName',
    'name': 'Name',
    'from': 'From',
    'to': 'To',
    'planned': 'Planned',
    'status': 'Status'
  };

  // Filter properties
  filtersExpanded: boolean = true;

  // Search properties
  selectedSearchType: string = 'registration';
  currentSearchValue: string = '';
  searchQuery: string = '';

  // Multi-select status
  @Input() selectedStatuses: number[] = [];
  selectedStatus: number | '' = '';

  // Date range FormGroups
  plannedDateRange: FormGroup;
  fromRange: FormGroup;
  toRange: FormGroup;
  @Input() predefinedPlannedDateRange: dateRange;
  @Input() predefinedFromRange: dateRange;
  @Input() predefinedToRange: dateRange;

  @Input() initialSortedColumn: string;
  @Input() initialSortDirection: SortDirection;

  additionalServiceStatusOptions: any[] = [
    { value: 1, name: "New" },
    { value: 2, name: "Paid" },
    { value: 4, name: "Planned" },
    { value: 8, name: "Underway" },
    { value: 16, name: "Completed" },
    { value: 32, name: "Removed" },
    { value: 64, name: "Cancelled" }
  ];

  @Input() selectedAdditionalServices: any[] = [];
  additionalServiceOptions: any[] = [];

  selectedParking: any[] = [];
  parkingOptions: any[] = [];

  getAdditionalServiceStatusColor(status: string): string {
    switch (status) {
      case 'New':
        return '#3f51b5';
      case 'Paid':
        return '#28a745';
      case 'Planned':
        return '#17a2b8';
      case 'Underway':
        return '#ffc107';
      case 'Completed':
        return '#6c757d';
      case 'Cancelled':
        return '#dc3545';
      case 'Removed':
        return '#F44336';
      default:
        return '#adb5bd';
    }
  }


  constructor(
    public adminService: AdminService,
    public backofficeService: BackOfficeService,
    public dialog: MatDialog,
    public router: Router,
    private fb: UntypedFormBuilder,
    public datepipe: DatePipe,
    public backOffice24StatesService: BackOffice24StatesService,
  ) {
    this.plannedDateRange = this.fb.group({
      start: [{ value: this.predefinedPlannedDateRange?.From || null, disabled: this.isLoading }],
      end: [{ value: this.predefinedPlannedDateRange?.To || null, disabled: this.isLoading }]
    });

    this.fromRange = this.fb.group({
      start: [{ value: this.predefinedFromRange?.From || null, disabled: this.isLoading }],
      end: [{ value: this.predefinedFromRange?.To || null, disabled: this.isLoading }]
    });

    this.toRange = this.fb.group({
      start: [{ value: this.predefinedToRange?.From || null, disabled: this.isLoading }],
      end: [{ value: this.predefinedToRange?.To || null, disabled: this.isLoading }]
    });

    // Subscribe to date range changes (but don't auto-trigger API calls)
    // this.createdDateRange.valueChanges.subscribe(() => {
    //   // Just update the filter count, don't auto-trigger API
    // });

    // this.bookingBeginRange.valueChanges.subscribe(() => {
    //   // Just update the filter count, don't auto-trigger API
    // });

    // this.bookingEndRange.valueChanges.subscribe(() => {
    //   // Just update the filter count, don't auto-trigger API
    // });
  }

  ngOnInit(): void {
    this.orgSubscription = this.backOffice24StatesService.currentOrganization$.subscribe(
      org => {
        this.organization = org;

        org.config.parkings.forEach((element: any) => {
          this.parkingOptions.push({
            value: element.parkingId,
            name: element.parkingName
          });
        });

        this.getOrganizationAdditionalServices().then(() => {
          this.setOrderMatTableData();
        });
      }
    )
  }

  getOrganizationAdditionalServices(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.incrementRequests.emit();
      this.backofficeService.getOrganizationAdditionalServices(this.organization.organizationId).pipe(
        takeUntil(this.destroy$)
      ).subscribe({
        next: (res: any) => {
          res.forEach((element: any) => {
            this.additionalServiceOptions.push({
              value: element.id,
              name: element.name
            });
          });
          this.decrementRequests.emit();
          resolve();
        },
        error: (error) => {
          this.decrementRequests.emit();
          reject(error);
        },
        complete: () => {
          this.decrementRequests.emit();
        }
      });
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.orgSubscription.unsubscribe();
  }

  onSearchTypeChange(): void {
    this.currentSearchValue = '';
    // Don't trigger API call automatically
  }

  executeSearch(): void {
    if (this.currentSearchValue?.trim()) {
      this.applyFilters();
    }
  }

  // Clear search
  clearSearch(): void {
    this.currentSearchValue = '';
    this.applyFilters();
  }


  getSearchPlaceholder(): string {
    switch (this.selectedSearchType) {
      case 'common': return 'ORDER_LIST.SEARCH_PLACEHOLDER_ANY_FIELD';
      case 'name': return 'ORDER_LIST.SEARCH_PLACEHOLDER_NAME';
      case 'email': return 'ORDER_LIST.SEARCH_PLACEHOLDER_EMAIL';
      case 'phone': return 'ORDER_LIST.SEARCH_PLACEHOLDER_PHONE';
      case 'registration': return 'ORDER_LIST.SEARCH_PLACEHOLDER_REGISTRATION';
      default: return 'ORDER_LIST.SEARCH_PLACEHOLDER';
    }
  }

  getSearchIcon(): string {
    switch (this.selectedSearchType) {
      case 'common': return 'search';
      case 'name': return 'person';
      case 'email': return 'email';
      case 'phone': return 'phone';
      case 'registration': return 'directions_car';
      default: return 'search';
    }
  }

  // Backward compatibility methods use if you want
  onSearchChange(event: any): void {
    this.searchQuery = event.target.value;
    this.applyFilters();
  }

  onStatusChange(): void {
    this.applyFilters();
  }

  onDateFilterChange(): void {
    this.applyFilters();
  }

  applyFilters(): void {
    // Cancel any ongoing requests
    this.destroy$.next();

    if (this.paginator) {
      this.paginator.pageIndex = 0;
    }

    // this.loadData();
    this.setOrderMatTableData();
  }

  // Clear all filters
  clearFilters(): void {
    this.currentSearchValue = '';
    this.searchQuery = '';
    this.selectedSearchType = 'registration';
    this.selectedStatuses = [];
    this.selectedStatus = '';
    this.plannedDateRange.reset();
    this.fromRange.reset();
    this.toRange.reset();
    this.applyFilters();
  }

  getActiveFiltersCount(): number {
    let count = 0;
    if (this.currentSearchValue?.trim()) count++;
    if (this.searchQuery?.trim()) count++;
    if (this.selectedStatuses.length > 0) count++;
    if (this.selectedStatus !== '') count++;
    if (this.plannedDateRange.get('start')?.value || this.plannedDateRange.get('end')?.value) count++;
    if (this.fromRange.get('start')?.value || this.fromRange.get('end')?.value) count++;
    if (this.toRange.get('start')?.value || this.toRange.get('end')?.value) count++;
    return count;
  }

  setFilters({
    orderIds = null,
    sortingColumn = this.initialSortedColumn,
    sortingDirection = this.initialSortDirection,
    perPage,
    page
  }: {
    orderIds?: string[] | null;
    sortingColumn?: string;
    sortingDirection?: SortDirection;
    perPage: number;
    page: number;
  }): AdditionalServicePaginationModel {
    const result: Partial<AdditionalServicePaginationModel> = {
      OrderIds: orderIds || undefined,
      SortingColumn: sortingColumn,
      SortingDirection: sortingDirection,
      Paginate: {
        PerPage: perPage,
        Page: page
      }
    };


    if (this.currentSearchValue?.trim()) {
      switch (this.selectedSearchType) {
        // case 'common':
        //   result.CommonSearch = this.currentSearchValue;
        //   break;
        // case 'name':
        //   result.NameSearch = this.currentSearchValue;
        //   break;
        // case 'email':
        //   result.EmailSearch = this.currentSearchValue;
        //   break;
        // case 'phone':
        //   result.PhoneSearch = this.currentSearchValue;
        //   break;
        case 'registration':
          result.LPSearch = this.currentSearchValue;
          break;
      }
    }

    // Backward compatibility for old search
    // if (this.searchQuery?.trim()) {
    //   result.CommonSearch = this.searchQuery;
    // }

    // Handle multi-select status
    if (this.selectedStatuses.length > 0) {
      result.Statuses = this.selectedStatuses;
    } else {
      result.Statuses = [];
    }

    // Handle multi-select status
    if (this.selectedAdditionalServices.length > 0) {
      result.AdditionalServiceIds = this.selectedAdditionalServices;
    } else {
      result.AdditionalServiceIds = this.additionalServiceOptions
        .filter(e => e.value !== "e22b31a3-4ee6-4c8f-a980-b2fe00c1aefe")
        .map(e => e.value);
    }

    if (this.selectedParking.length > 0) {
      result.ParkingIds = this.selectedParking;
    } else {
      result.ParkingIds = [];
    }

    // Backward compatibility for single status
    // if (this.selectedStatus !== '') {
    //   result.Status = this.selectedStatus;
    // }

    // Handle date ranges from FormGroups
    const plannedStart = this.plannedDateRange.get('start')?.value;
    const plannedEnd = this.plannedDateRange.get('end')?.value;
    if (plannedStart || plannedEnd) {
      result.Planned = {
        From: plannedStart ? this.datepipe.transform(plannedStart, 'yyyy-MM-dd') : null,
        To: plannedEnd ? this.datepipe.transform(plannedEnd, 'yyyy-MM-dd') : null
      };
    }

    const fromStart = this.fromRange.get('start')?.value;
    const fromEnd = this.fromRange.get('end')?.value;
    if (fromStart || fromEnd) {
      result.FromUtc = {
        From: fromStart ? this.datepipe.transform(fromStart, 'yyyy-MM-dd') : null,
        To: fromEnd ? this.datepipe.transform(fromEnd, 'yyyy-MM-dd') : null
      };
    }

    const toStart = this.toRange.get('start')?.value;
    const toEnd = this.toRange.get('end')?.value;
    if (toStart || toEnd) {
      result.ToUtc = {
        From: toStart ? this.datepipe.transform(toStart, 'yyyy-MM-dd') : null,
        To: toEnd ? this.datepipe.transform(toEnd, 'yyyy-MM-dd') : null
      };
    }

    return result as AdditionalServicePaginationModel;
  }

  // setLoadingState(isLoading: boolean) {
  //   this.isLoading = isLoading;
  // }

  // private incrementRequests() {
  //   this.activeRequests++;
  //   this.setLoadingState(true);
  // }

  // private decrementRequests() {
  //   if (this.activeRequests > 0)
  //     this.activeRequests--;


  setOrderMatTableData() {
    if (!this.organization) return;

    // Initialize data source
    // this.dataSource = new MatTableDataSource<Order>(this.orders);
    // this.dataSource.paginator = this.paginator;

    // Set up pagination changes
    if (this.paginator) {
      this.paginator.page.pipe(
        takeUntil(this.destroy$)
      ).subscribe(() => {
        this.loadData();
      });
    }

    this.setupSorting();

    // Initial load
    this.loadData();
  }

  private setupSorting() {
    if (this.sort) {
      // Set default sort
      if (!this.sort.active) {
        this.sort.active = this.initialSortedColumn;
        this.sort.direction = this.initialSortDirection;
        this.sort.sortChange.emit({
          active: this.sort.active,
          direction: this.sort.direction
        });
      }

      // Listen for sort changes
      this.sort.sortChange.pipe(
        takeUntil(this.destroy$)
      ).subscribe(() => {
        if (this.paginator) {
          this.paginator.pageIndex = 0; // Reset to first page on sort
        }
        this.loadData();
      });
    }
  }

  private loadData() {
    if (!this.organization) return;

    // Get current sort state
    const sortActive = this.sort?.active || this.initialSortedColumn;
    const sortDirection = this.sort?.direction || this.initialSortDirection;

    // Map to backend column name
    const sortColumn = this.columnMapping[sortActive] || sortActive;

    this.incrementRequests.emit();

    this.backofficeService.getPaginatedAdditionalServiceTasks(
      this.organization.organizationId,
      this.setFilters({
        perPage: this.paginator?.pageSize || this.pageSizes[0],
        page: this.paginator ? this.paginator.pageIndex + 1 : 1,
        sortingColumn: sortColumn,
        sortingDirection: sortDirection as 'asc' | 'desc'
      })
    ).pipe(
      takeUntil(this.destroy$),
      catchError(error => {
        console.error('Error loading orders:', error);
        return of({ data: [], pagination: { total: 0 } });
      }),
      finalize(() => this.decrementRequests.emit())
    ).subscribe((response: any) => {
      if (!response) return;

      this.orders = response.data || [];
      this.totalData = response.pagination?.total || 0;

      // Process customer data
      this.orders.forEach(order => {
        if (order.customer) {
          order.customerName = `${order.customer.firstName} ${order.customer.lastName}`;
          order.cellular = order.customer.cellular;
          order.email = order.customer.email;
        }
      });

      // Update data source
      if (!this.dataSource) {
        this.dataSource = new MatTableDataSource<Order>(this.orders);
        this.dataSource.paginator = this.paginator;
      } else {
        this.dataSource.connect().next(this.orders);
      }
    });
  }

  goToOrder(orderId: string): void {
    const url = this.router.serializeUrl(this.router.createUrlTree([`/admin/back-office24-order/${orderId}`]));
    window.open(url, '_blank');
  }

  toggleFilters(): void {
    this.filtersExpanded = !this.filtersExpanded;
  }
}
